import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { ClipboardPlus, Plus, Pill, Activity, X, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { API } from "@/config/constants";
import { useOfflineData } from "@/contexts/OfflineDataContext";

const ROUTES = ["IV", "IM", "IN", "IO", "PO", "SL", "SC", "Inhaled"];
const DOSE_UNITS = ["g", "mg", "mcg", "mcg/kg/min", "mg/min", "mcg/min", "mL", "mL/hr"];

const COMMON_INTERVENTIONS = [
  "12-Lead ECG", "IV Access", "IO Access", "BGL Check", "SpO2 Monitor",
  "BP Check", "Airway Management", "BVM Ventilation", "CPAP Applied",
  "Splint Applied", "Tourniquet Applied", "Defibrillation", "Cardioversion",
  "Transcutaneous Pacing", "Suction", "NPA Inserted", "OPA Inserted",
];

function nowTime() {
  const d = new Date();
  return `${String(d.getHours()).padStart(2, "0")}${String(d.getMinutes()).padStart(2, "0")}`;
}

function extractDrugsFromProtocol(protocol) {
  if (!protocol?.care_levels) return [];
  const drugPattern = /^(Acetaminophen|Adenosine|Amiodarone|Aspirin|Atropine|Calcium Gluconate|Ca Gluconate|Dexamethasone|Dextrose|Diazepam|Dimenhydrinate|Diphenhydramine|Dopamine|Epinephrine|Fentanyl|Furosemide|Glucagon|Haloperidol|Hydrocortisone|Hydromorphone|Ibuprofen|Ipratropium|Ketamine|Ketorolac|Labetalol|Lidocaine|Lorazepam|Magnesium Sulfate|Metoprolol|Midazolam|Morphine|Naloxone|Nitroglycerin|Nitrous Oxide|Norepinephrine|Ondansetron|Oxytocin|Propofol|Rocuronium|Salbutamol|Sodium Bicarbonate|Succinylcholine|Tenecteplase|Tranexamic Acid|Vasopressin|Ventolin)/i;
  const found = new Set();
  for (const cl of protocol.care_levels) {
    for (const iv of cl.interventions || []) {
      const m = iv.text?.match(drugPattern);
      if (m) found.add(m[1]);
      for (const s of iv.sub_steps || []) {
        const ms = s.match(drugPattern);
        if (ms) found.add(ms[1]);
      }
    }
  }
  return Array.from(found).sort();
}

export default function InterventionQuickAdd({ protocol }) {
  const [open, setOpen] = useState(false);
  const [activeLog, setActiveLog] = useState(null);
  const [selectedType, setSelectedType] = useState(null);
  const [name, setName] = useState("");
  const [doseValue, setDoseValue] = useState("");
  const [doseUnit, setDoseUnit] = useState("mg");
  const [route, setRoute] = useState("IV");
  const [time, setTime] = useState(nowTime());
  const [loading, setLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const { drugs: cachedDrugs } = useOfflineData();

  const suggestedDrugs = extractDrugsFromProtocol(protocol);
  const allDrugNames = useMemo(() => cachedDrugs.map((d) => d.name).sort(), [cachedDrugs]);
  const drugSet = useMemo(() => new Set(allDrugNames.map(d => d.toLowerCase())), [allDrugNames]);

  const filtered = useMemo(() => {
    const q = name.trim().toLowerCase();
    const results = [];
    // Drugs
    const drugMatches = !q
      ? allDrugNames.slice(0, 6)
      : allDrugNames.filter((d) => {
          if (d.toLowerCase().includes(q)) return true;
          const brandMatch = d.match(/\(([^)]+)\)/);
          return brandMatch && brandMatch[1].split(/,\s*/).some(a => a.trim().toLowerCase().includes(q));
        });
    drugMatches.forEach(d => results.push({ name: d, type: "drug" }));
    // Interventions
    const interventions = !q
      ? COMMON_INTERVENTIONS.slice(0, 4)
      : COMMON_INTERVENTIONS.filter(i => i.toLowerCase().includes(q));
    interventions.forEach(i => results.push({ name: i, type: "intervention" }));
    return results.slice(0, 10);
  }, [name, allDrugNames]);

  const selectSuggestion = (item) => {
    setName(item.name);
    setSelectedType(item.type);
    setShowSuggestions(false);
  };

  const isDrug = selectedType === "drug" || (!selectedType && name && drugSet.has(name.trim().toLowerCase()));

  useEffect(() => {
    if (open) {
      setTime(nowTime());
      // Check for active log
      axios.get(`${API}/intervention-logs?status=active`).then((res) => {
        if (res.data.length > 0) setActiveLog(res.data[0]);
      }).catch(() => {});
    }
  }, [open]);

  const startAndAdd = async () => {
    setLoading(true);
    try {
      let log = activeLog;
      if (!log) {
        const res = await axios.post(`${API}/intervention-logs`, {
          patient_label: `Trip ${new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`,
        });
        log = res.data;
        setActiveLog(log);
      }
      await axios.post(`${API}/intervention-logs/${log.id}/entries`, {
        type: selectedType || (drugSet.has(name.trim().toLowerCase()) ? "drug" : "intervention"),
        name: name.trim(), dose: doseValue ? `${doseValue} ${doseUnit}` : "", route, time,
      });
      toast.success(`${name} logged at ${time.slice(0, 2)}:${time.slice(2)}`);
      setName("");
      setSelectedType(null);
      setDoseValue("");
      setDoseUnit("mg");
      setRoute("IV");
      setTime(nowTime());
    } catch (e) {
      toast.error("Failed to log intervention");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="gap-1 border-emerald-500/30 text-emerald-500 hover:bg-emerald-500/10"
          data-testid="quick-add-intervention-btn"
        >
          <ClipboardPlus className="w-4 h-4" />
          <span className="hidden sm:inline">Log</span>
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <ClipboardPlus className="w-5 h-5 text-emerald-500" />
            Quick Log
            {activeLog && (
              <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30 text-xs ml-2">
                {activeLog.patient_label}
              </Badge>
            )}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          {/* Protocol drug suggestions */}
          {suggestedDrugs.length > 0 && !name && (
            <div className="flex flex-wrap gap-1">
              {suggestedDrugs.map((d) => (
                <button
                  key={d}
                  type="button"
                  className="text-xs px-2 py-1 rounded-full border transition-colors bg-amber-500/10 text-amber-400 border-amber-500/20 hover:bg-amber-500/20"
                  onClick={() => { setName(d); setSelectedType("drug"); }}
                >
                  {d}
                </button>
              ))}
            </div>
          )}

          <div className="relative">
            <Input
              placeholder="Search drug or intervention..."
              value={name}
              onChange={(e) => { setName(e.target.value); setSelectedType(null); setShowSuggestions(true); }}
              onFocus={() => setShowSuggestions(true)}
              onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
              data-testid="quick-entry-name"
              autoFocus
            />
            {showSuggestions && filtered.length > 0 && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-popover border border-border rounded-lg shadow-lg z-50 max-h-48 overflow-auto">
                {filtered.map((item) => (
                  <button
                    key={`${item.type}-${item.name}`}
                    className="w-full px-3 py-2 text-left text-sm hover:bg-accent flex items-center gap-2 first:rounded-t-lg last:rounded-b-lg"
                    onMouseDown={() => selectSuggestion(item)}
                  >
                    {item.type === "drug" ? (
                      <Pill className="w-3.5 h-3.5 text-amber-400 shrink-0" />
                    ) : (
                      <Activity className="w-3.5 h-3.5 text-blue-400 shrink-0" />
                    )}
                    <span className="flex-1">{item.name}</span>
                    <span className="text-xs text-muted-foreground">{item.type === "drug" ? "Drug" : "Intervention"}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {isDrug && (
            <div className="flex gap-2 items-center">
              <Input
                placeholder="Dose"
                value={doseValue}
                onChange={(e) => setDoseValue(e.target.value)}
                className="w-20"
                data-testid="quick-entry-dose"
              />
              <select
                value={doseUnit}
                onChange={(e) => setDoseUnit(e.target.value)}
                className="h-9 rounded-md border border-border bg-background px-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                data-testid="quick-entry-dose-unit"
              >
                {DOSE_UNITS.map((u) => (
                  <option key={u} value={u}>{u}</option>
                ))}
              </select>
              <select
                value={route}
                onChange={(e) => setRoute(e.target.value)}
                className="h-9 rounded-md border border-border bg-background px-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                data-testid="quick-entry-route"
              >
                {ROUTES.map((r) => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
            </div>
          )}

          <div className="flex gap-2">
            <Input
              placeholder="Time (HHMM)"
              value={time}
              onChange={(e) => setTime(e.target.value.replace(/\D/g, "").slice(0, 4))}
              className="w-24 font-mono text-center"
              data-testid="quick-entry-time"
            />
            <Button
              className="flex-1 bg-emerald-600 hover:bg-emerald-700"
              onClick={startAndAdd}
              disabled={!name.trim() || loading}
              data-testid="quick-add-btn"
            >
              <Plus className="w-4 h-4 mr-1" />
              {activeLog ? "Add to Log" : "Start Log & Add"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
